import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    IconButton,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Box,
    Collapse,
    Card,
    CardContent
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import SettingsIcon from '@material-ui/icons/Settings';
import InfoIcon from '@material-ui/icons/Info';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
    getFontSizeSetting,
    getFontSizeOptions,
    getScaledFontSize
} from '../../utils/fontSizeManager';
import { useFontSize } from '../../contexts/FontSizeContext';
import lng from '../../misc/lng';

const { chiLng } = lng;

const useStyles = makeStyles((theme) => ({
    drawer: {
        '& .MuiDrawer-paper': {
            width: '50vw',
            maxWidth: 350,
            minWidth: 280,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            // On mobile devices, use a bit more width for better usability
            [theme.breakpoints.down('sm')]: {
                width: '70vw',
                maxWidth: 300,
            },
        },
    },
    drawerContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2),
        backgroundColor: '#666666', // Match app's primary color
        color: '#FFFFFF',
        minHeight: 64,
    },
    tableInfo: {
        padding: theme.spacing(2),
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
    },
    tableTitle: {
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: '#666666',
        marginBottom: theme.spacing(0.5),
    },
    tableDetails: {
        fontSize: '0.9rem',
        color: '#666666',
    },
    title: {
        flex: 1,
        fontWeight: 'bold',
        fontSize: '1.25rem',
    },
    closeButton: {
        color: '#FFFFFF',
    },
    menuList: {
        padding: 0,
        flex: 1,
        overflow: 'auto',
    },
    sectionCard: {
        margin: theme.spacing(1, 2, 2, 2),
        backgroundColor: '#f9f9f9',
    },
    sectionTitle: {
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
    },
    radioGroup: {
        marginTop: theme.spacing(1),
    },
    radioOption: {
        margin: theme.spacing(0.5, 0),
        '& .MuiFormControlLabel-label': {
            fontSize: '1rem',
        },
    },
    previewSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: '#f0f0f0',
        borderRadius: 8,
    },
    previewTitle: {
        fontSize: '1rem',
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    previewText: {
        marginBottom: theme.spacing(1),
    },
    previewSmall: {
        fontSize: '0.875rem',
    },
    previewMedium: {
        fontSize: '1rem',
    },
    previewLarge: {
        fontSize: '1.125rem',
    },
    previewButton: {
        fontSize: '1rem',
        fontWeight: 'bold',
    },
    bottomSection: {
        marginTop: 'auto',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
    },
    fontSizeSection: {
        padding: theme.spacing(2),
    },
}));

const MenuDrawer = ({ open, onClose, onFontSizeChange, tableName, tableId }) => {
    const classes = useStyles();
    const { currentSetting, changeFontSize } = useFontSize();
    const [selectedSize, setSelectedSize] = useState('medium');
    const [expandedSections, setExpandedSections] = useState({
        display: false,
        general: false,
        about: false
    });

    useEffect(() => {
        setSelectedSize(currentSetting);
    }, [currentSetting, open]);

    const handleSizeChange = (event) => {
        const newSize = event.target.value;
        setSelectedSize(newSize);
        changeFontSize(newSize); // Use context instead of direct function

        // Notify parent component about the change
        if (onFontSizeChange) {
            onFontSizeChange(newSize);
        }
    };

    const handleSectionToggle = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const fontSizeOptions = getFontSizeOptions();

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            className={classes.drawer}
        >
            {/* Header */}
            <div className={classes.header}>
                <Typography className={classes.title}>
                    ALL DAY
                </Typography>
                <IconButton
                    className={classes.closeButton}
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </div>

            {/* Table Information */}
            <div className={classes.tableInfo}>
                <Typography className={classes.tableTitle}>
                    {tableName === '外帶' ? chiLng.takeoutOrdering : chiLng.dineInOrdering}
                </Typography>
                {tableName !== '外帶' && (
                    <Typography className={classes.tableDetails}>
                        {chiLng.tableNumber}: {tableName || tableId || '未知'}
                    </Typography>
                )}
            </div>

            <div className={classes.drawerContent}>
                {/* Main Content */}
                <List className={classes.menuList}>
                    {/* General Settings Section */}
                    {/* <ListItem button onClick={() => handleSectionToggle('general')}>
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary={chiLng.generalSettings} />
                        {expandedSections.general ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItem> */}

                    <Collapse in={expandedSections.general} timeout="auto" unmountOnExit>
                        <Card className={classes.sectionCard}>
                            <CardContent>
                                <Typography variant="body2" color="textSecondary">
                                    更多設定選項即將推出...
                                </Typography>
                            </CardContent>
                        </Card>
                    </Collapse>

                    <Divider />

                    {/* About Section */}
                    <ListItem button onClick={() => handleSectionToggle('about')}>
                        <ListItemIcon>
                            <InfoIcon />
                        </ListItemIcon>
                        <ListItemText primary={chiLng.about} />
                        {expandedSections.about ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItem>

                    <Collapse in={expandedSections.about} timeout="auto" unmountOnExit>
                        <Card className={classes.sectionCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    ALL DAY 點餐系統
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {chiLng.version}: 1.0.0
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    快速、便捷的餐廳點餐解決方案
                                </Typography>
                            </CardContent>
                        </Card>
                    </Collapse>
                </List>

                {/* Font Size Section at Bottom */}
                <div className={classes.bottomSection}>
                    <div className={classes.fontSizeSection}>
                        <Typography className={classes.sectionTitle}>
                            {chiLng.fontSizeSettings}
                        </Typography>

                        <FormControl component="fieldset" className={classes.radioGroup}>
                            <RadioGroup
                                value={selectedSize}
                                onChange={handleSizeChange}
                                row
                            >
                                {fontSizeOptions.map((option) => (
                                    <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        control={<Radio color="primary" size="small" />}
                                        label={option.label}
                                        className={classes.radioOption}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>

                        {/* Preview */}
                        <Box mt={2} p={1} bgcolor="#f0f0f0" borderRadius={1}>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ fontSize: getScaledFontSize('caption') }}
                                gutterBottom
                            >
                                預覽:
                            </Typography>
                            <Typography style={{ fontSize: getScaledFontSize('mealTitle') }}>
                                餐點名稱
                            </Typography>
                            <Typography style={{ fontSize: getScaledFontSize('priceMain'), color: '#d2691e', fontWeight: '600' }}>
                                $25.00
                            </Typography>
                        </Box>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default MenuDrawer;
